import type { MrdDepartmentRow, MrdDetailNumberField, MrdDetailState, MrdPatientRule } from './types';

type MrdDetailSection = 'pathology' | 'radiology' | 'ot';

export interface MrdStaticIdMap {
  label: string;
  collection: 'departments' | 'charge_categories';
  id: string;
}

export interface MrdDetailFieldMap {
  key: MrdDetailNumberField;
  label: string;
  section: MrdDetailSection;
  collection: 'charge_categories';
  chargeCategoryId: string;
}

export interface MrdDepartmentConditionMap extends MrdPatientRule {
  department: string;
  departmentId: string;
  genders: string[];
  minAgeYears: number | null;
  maxAgeYears: number | null;
  oldPatientRatio: number;
  newPatientRatio: number;
  malePatientRatio: number;
  femalePatientRatio: number;
  bedCount: number;
}

export interface MrdGeneratedEntry {
  departmentName: string;
  departmentId: string;
  departmentMapped: boolean;
  fieldName: string;
  fieldKey: MrdDetailNumberField;
  collection: 'charge_categories';
  mappedId: string;
  mapped: boolean;
  count: number;
}

export const mrdDepartmentIdMaps: MrdStaticIdMap[] = [
  { label: 'DERMATOLOGY', collection: 'departments', id: '6c129b5ded87ffb67955b6f7' },
  { label: 'GENERAL MEDICINE', collection: 'departments', id: '47b7bc5e76ba66afe490db24' },
  { label: 'GENERAL SURGERY', collection: 'departments', id: 'b90d8771ce64e02de29d4e85' },
  { label: 'ICUs', collection: 'departments', id: '9000cf0d6d446a884a52702f' },
  { label: 'OBSTETRICS & GYNAECOLOGY', collection: 'departments', id: '613e8724649f2167d0172f60' },
  { label: 'OPTHALMOLOGY', collection: 'departments', id: 'a232c56d6d5d7a438ff44054' },
  { label: 'ORTHOPEDICS', collection: 'departments', id: 'dd76c75f5effe095f2d9c21b' },
  { label: 'OTORHINOLARYNGOLOGY (ENT)', collection: 'departments', id: '0961d39a121dd7695eedf0db' },
  { label: 'PEDIATRICS', collection: 'departments', id: '294de976916012d5aac29520' },
  { label: 'PSYCHIATRY', collection: 'departments', id: '589a96ac8732cfbec3da3622' },
];

export const mrdPatientSourceRatios = { oldPatientRatio: 31, newPatientRatio: 69 } as const;
export const mrdGenderRatios = { malePatientRatio: 68, femalePatientRatio: 32 } as const;

export const mrdDepartmentConditionMaps: MrdDepartmentConditionMap[] = [
  { department: 'DERMATOLOGY', departmentId: '6c129b5ded87ffb67955b6f7', genders: ['any'], minAgeYears: 0, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 10 },
  { department: 'GENERAL MEDICINE', departmentId: '47b7bc5e76ba66afe490db24', genders: ['any'], minAgeYears: 15, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 150 },
  { department: 'GENERAL SURGERY', departmentId: 'b90d8771ce64e02de29d4e85', genders: ['any'], minAgeYears: 0, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 150 },
  { department: 'ICUs', departmentId: '9000cf0d6d446a884a52702f', genders: ['any'], minAgeYears: 0, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 30 },
  { department: 'OBSTETRICS & GYNAECOLOGY', departmentId: '613e8724649f2167d0172f60', genders: ['female'], minAgeYears: 11, maxAgeYears: 100, ...mrdPatientSourceRatios, malePatientRatio: 0, femalePatientRatio: 100, bedCount: 75 },
  { department: 'OPTHALMOLOGY', departmentId: 'a232c56d6d5d7a438ff44054', genders: ['any'], minAgeYears: 5, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 20 },
  { department: 'ORTHOPEDICS', departmentId: 'dd76c75f5effe095f2d9c21b', genders: ['any'], minAgeYears: 3, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 60 },
  { department: 'OTORHINOLARYNGOLOGY (ENT)', departmentId: '0961d39a121dd7695eedf0db', genders: ['any'], minAgeYears: 5, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 20 },
  { department: 'PEDIATRICS', departmentId: '294de976916012d5aac29520', genders: ['any'], minAgeYears: 0, maxAgeYears: 10, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 75 },
  { department: 'PSYCHIATRY', departmentId: '589a96ac8732cfbec3da3622', genders: ['any'], minAgeYears: 12, maxAgeYears: 100, ...mrdPatientSourceRatios, ...mrdGenderRatios, bedCount: 15 },
];

export const mrdDetailFieldMaps: MrdDetailFieldMap[] = [
  { key: 'pathologyBiochemistry', label: 'Biochemistry', section: 'pathology', collection: 'charge_categories', chargeCategoryId: '20215b6e729c1e02932bfaa1' },
  { key: 'pathologyHaematology', label: 'Haematology', section: 'pathology', collection: 'charge_categories', chargeCategoryId: 'fda1c0cacdd03fc5c2585eff' },
  { key: 'pathologyClinicalPathology', label: 'Clinical Pathology', section: 'pathology', collection: 'charge_categories', chargeCategoryId: 'fc46ad6e2350c4ffe5341f27' },
  { key: 'pathologyHistopathology', label: 'Histopathology', section: 'pathology', collection: 'charge_categories', chargeCategoryId: '3a48ca8a7ad230567c18a2c2' },
  { key: 'pathologyCytology', label: 'Cytology', section: 'pathology', collection: 'charge_categories', chargeCategoryId: '20d425d1590139cf4c686fbc' },
  { key: 'pathologyCs', label: 'C/S', section: 'pathology', collection: 'charge_categories', chargeCategoryId: 'a81a50c33bb7940937c66f5a' },
  { key: 'pathologySerology', label: 'Serology', section: 'pathology', collection: 'charge_categories', chargeCategoryId: 'ab7ce558a16563c5bd7b33b8' },
  { key: 'radiologyXray', label: 'X-Ray', section: 'radiology', collection: 'charge_categories', chargeCategoryId: '63e7a2fb4c6d3c633c7dc554' },
  { key: 'radiologyUsg', label: 'Ultrasonography', section: 'radiology', collection: 'charge_categories', chargeCategoryId: '178753069779172221c92e6e' },
  { key: 'radiologyCtRoutine', label: 'CT Routine', section: 'radiology', collection: 'charge_categories', chargeCategoryId: 'd01f70404c3d107d6f7ec847' },
  { key: 'radiologyMri', label: 'MRI', section: 'radiology', collection: 'charge_categories', chargeCategoryId: '2f58d1a653408a50182d3672' },
];

const normalizeMapLabel = (value: string) => value.trim().replace(/\s+/g, ' ').toUpperCase();

const departmentIdByName = new Map(mrdDepartmentIdMaps.map((entry) => [normalizeMapLabel(entry.label), entry.id]));

const isSectionEnabled = (section: MrdDetailSection, detail: MrdDetailState) => {
  if (section === 'pathology') return detail.pathologyEnabled;
  if (section === 'radiology') return detail.radiologyEnabled;
  return detail.otSectionEnabled;
};

export const mrdDepartmentMappedId = (row: MrdDepartmentRow) => departmentIdByName.get(normalizeMapLabel(row.departmentName)) ?? '';

export const mrdDepartmentConditionFor = (row: MrdDepartmentRow): MrdPatientRule | undefined => {
  const entry = mrdDepartmentConditionMaps.find((candidate) => normalizeMapLabel(candidate.department) === normalizeMapLabel(row.departmentName));
  if (!entry) return undefined;
  return {
    genders: entry.genders,
    minAgeYears: entry.minAgeYears,
    maxAgeYears: entry.maxAgeYears,
    oldPatientRatio: entry.oldPatientRatio,
    newPatientRatio: entry.newPatientRatio,
    malePatientRatio: entry.malePatientRatio,
    femalePatientRatio: entry.femalePatientRatio,
  };
};

export const buildMrdGeneratedEntries = (row: MrdDepartmentRow, detail: MrdDetailState): MrdGeneratedEntry[] => {
  const mappedDepartmentId = mrdDepartmentMappedId(row);
  return mrdDetailFieldMaps.flatMap((field) => {
    const count = detail[field.key];
    if (!isSectionEnabled(field.section, detail) || count <= 0) return [];
    return [{
      departmentName: row.departmentName,
      departmentId: mappedDepartmentId || row.departmentId,
      departmentMapped: Boolean(mappedDepartmentId),
      fieldName: field.label,
      fieldKey: field.key,
      collection: field.collection,
      mappedId: field.chargeCategoryId,
      mapped: Boolean(field.chargeCategoryId),
      count,
    }];
  });
};
